/**
 * Form URL Sync
 *
 * Keep filter forms (on office list pages that use `hx-push-url="true"`)
 * visually in sync with the URL across three different restore paths:
 *
 *   A. bfcache restore (pageshow, event.persisted === true)
 *      Triggered when the next page was NOT an hx-boost navigation
 *      (e.g. the user left the tab, came back via a regular link, or
 *      the browser put the page into bfcache). IDL properties on
 *      inputs may or may not survive — re-sync from URL.
 *
 *   B. HTMX hx-boost Back/Forward (htmx:historyRestore)
 *      With `hx-boost="true"` on #main-content, the filter list → detail
 *      navigation is a boosted swap. HTMX stores a snapshot of
 *      #main-content (via outerHTML serialization) and restores it on
 *      Back. outerHTML reflects HTML attributes, NOT IDL state, so a
 *      user-picked <select> value / typed input / checked toggle that
 *      was never written back to attributes gets lost.
 *
 *      We solve this on TWO sides:
 *        - BEFORE save (htmx:beforeHistorySave): reflect current IDL
 *          state to attributes so the snapshot is faithful.
 *        - AFTER restore (htmx:historyRestore): re-sync from URL as a
 *          safety net, in case the snapshot pre-dates the last filter
 *          change (e.g. older cached entry in htmx history cache).
 *
 *   C. First load (regular GET)
 *      Server-rendered `selected` / `value="…"` / `checked` attributes
 *      already match the URL — nothing to do.
 *
 * URL is the single source of truth for which filters are active; the
 * form IDL/attributes are just a visual mirror.
 *
 * Scoped intentionally:
 *   - Only touches `form[hx-push-url="true"]`.
 *   - Leaves purely-presentational / picker-visible inputs alone UNLESS
 *     we're reflecting state (where they DO matter: e.g. the customer
 *     autocomplete visible text).
 *   - Updates the matching `.custom-select-wrapper .custom-select-text`
 *     label when a select changes, so Flowbite-style custom selects
 *     stay visually consistent after URL re-sync.
 */

function syncSelect(select, params) {
    const name = select.name;
    if (!name) return;

    let targetValue;
    if (params.has(name)) {
        targetValue = params.get(name);
    } else {
        const defaultOption = Array.from(select.options).find(o => o.defaultSelected);
        targetValue = defaultOption ? defaultOption.value : (select.options[0] ? select.options[0].value : '');
    }

    if (select.value !== targetValue) {
        select.value = targetValue;
    }

    const wrapper = select.closest('.custom-select-wrapper');
    if (wrapper) {
        const textEl = wrapper.querySelector('.custom-select-text');
        const selectedOption = select.options[select.selectedIndex];
        if (textEl && selectedOption) {
            textEl.textContent = selectedOption.text;
        }
    }
}

function syncCheckbox(input, params) {
    const name = input.name;
    if (!name) return;

    let shouldBeChecked;
    if (params.has(name)) {
        const urlVal = params.get(name);
        shouldBeChecked = urlVal === input.value || urlVal === '1' || urlVal === 'on' || urlVal === 'true';
    } else {
        shouldBeChecked = input.defaultChecked;
    }

    if (input.checked !== shouldBeChecked) {
        input.checked = shouldBeChecked;
    }
}

function syncTextLikeInput(input, params) {
    const name = input.name;
    if (!name) return;

    const target = params.has(name) ? params.get(name) : input.defaultValue;
    if (input.value !== target) {
        input.value = target;
    }
}

function syncForm(form) {
    const params = new URLSearchParams(window.location.search);

    form.querySelectorAll('select[name]').forEach(sel => syncSelect(sel, params));

    form.querySelectorAll('input[name]').forEach(input => {
        const type = (input.type || '').toLowerCase();
        if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'file' || type === 'image' || type === 'reset') {
            return;
        }
        if (type === 'checkbox' || type === 'radio') {
            syncCheckbox(input, params);
            return;
        }
        syncTextLikeInput(input, params);
    });
}

/**
 * Mirror every input IDL property back onto its HTML attribute.
 *
 *   - <select>: set/remove `selected` on each <option> so the chosen
 *     option survives outerHTML serialization.
 *   - <input type=checkbox|radio>: set/remove `checked` attribute.
 *   - other text-like inputs (including nameless visible pickers like
 *     #customer-search): set `value=""` attribute to current value.
 *
 * Called on htmx:beforeHistorySave so the #main-content snapshot HTMX
 * stashes in its history cache reflects the user's current filter UI,
 * not the server-rendered defaults.
 */
function reflectIdlToAttributes(root) {
    root.querySelectorAll('form[hx-push-url="true"]').forEach(form => {
        form.querySelectorAll('select').forEach(sel => {
            Array.from(sel.options).forEach(opt => {
                if (opt.selected) {
                    opt.setAttribute('selected', '');
                } else {
                    opt.removeAttribute('selected');
                }
            });
        });

        form.querySelectorAll('input').forEach(input => {
            const type = (input.type || '').toLowerCase();
            if (type === 'submit' || type === 'button' || type === 'reset' || type === 'image' || type === 'file') {
                return;
            }
            if (type === 'checkbox' || type === 'radio') {
                if (input.checked) {
                    input.setAttribute('checked', '');
                } else {
                    input.removeAttribute('checked');
                }
                return;
            }
            input.setAttribute('value', input.value);
        });
    });
}

function handlePageShow(event) {
    if (!event.persisted) return;
    document.querySelectorAll('form[hx-push-url="true"]').forEach(syncForm);
}

function handleBeforeHistorySave() {
    const mc = document.getElementById('main-content') || document.body;
    reflectIdlToAttributes(mc);
}

function handleHistoryRestore() {
    document.querySelectorAll('form[hx-push-url="true"]').forEach(syncForm);
}

export function initFormUrlSync() {
    window.addEventListener('pageshow', handlePageShow);
    document.body.addEventListener('htmx:beforeHistorySave', handleBeforeHistorySave);
    document.body.addEventListener('htmx:historyRestore', handleHistoryRestore);
}
